import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Flex, List, Checkbox } from 'antd';
import styled from 'styled-components';
import Icon from './Icon';
const AlphabetFilterContainer = styled.div `
  display: flex;
  flex-direction: column;
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  scrollbar-width: none;
`;
const AlphabetFilterItem = styled.div `
  cursor: pointer;
  padding: 5px;
  font-size: 9.5px;
  vertical-align: top;
  position: relative;
  cursor: pointer;
  font-weight: ${({ active }) => (active ? 'bold' : 'normal')};
  color: ${({ active, theme }) => (active ? '#1890ff' : theme.textEmphasis.muted)};

  &:hover {
    color: #1890ff;
  }

  &::before {
    content: ${({ noDot }) => (noDot ? 'none' : '""')};
    width: 3px;
    height: 3px;
    background-color: ${({ theme }) => theme.textEmphasis.muted};
    border-radius: 50%;
    position: absolute;
    bottom: 0px;
    left: 50%;
    transform: translateX(-50%);
  }
`;
const ListContainer = styled.div `
  display: flex;
  height: 100%;
  position: absolute;
  width: 100%;
`;
const ListWrapper = styled.div `
  flex-grow: 1;
  padding: 0 16px 16px;
  overflow-y: auto;
  max-height: 100%;
  -ms-overflow-style: none;
  scrollbar-width: none;
  pointer-events: none;
  &::-webkit-scrollbar {
    display: none;
  }

  .bc-list {
    pointer-events: all;
    .bc-list-item {
      .bc-checkbox .bc-checkbox-inner {
        width: 16px;
        height: 16px;
        --bc-border-radius-sm: 6px;
        --bc-color-border: hsla(0, 0%, 0%, 0.07);
      }
      h4 {
        font-size: 16px;
        font-weight: 700;
        color: hsla(0, 0%, 33.33%, 1);
        padding-bottom: 1px;
        margin: 0;
      }

      p {
        font-size: 12px;
        color: hsla(0, 0%, 33.33%, 0.85);
        margin: 0;
      }
      .bc-list-item-action {
        opacity: 0;
      }
      &:hover {
        h4 {
          color: ${({ theme }) => theme.colors.primary};
        }
        .bc-list-item-action {
          opacity: 1;
        }
      }
    }
  }

  & > div {
    pointer-events: none;
  }
`;
const AlphabetHeader = styled.span `
  text-align: left;
  width: 100%;
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.textEmphasis.muted};
  position: sticky;
  z-index: 2;
  border-radius: 5px;
  top: 0;
  background-color: #fff;
  background: ${({ theme }) => theme.colors.gray200};
  background: linear-gradient(94deg, ${({ theme }) => theme.colors.gray200} 0%, rgba(255, 255, 255, 1) 100%);
  ${({ isBlinking }) => isBlinking &&
    ` 
    animation: blink 1s linear;
  `}

  @keyframes blink {
    0%,
    50%,
    100% {
      background: linear-gradient(94deg, ${({ theme }) => theme.colors.gray200} 0%, rgba(255, 255, 255, 1) 100%);
    }
    30%,
    80% {
      background: linear-gradient(94deg, #97d7a8 0%, rgba(255, 255, 255, 1) 100%);
    }
  }
  padding: 2px 8px;
`;
const AlphabetFilter = ({ onFilter, activeLetter }) => {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    const filterOptions = [...alphabet, '#'];
    return (_jsx(AlphabetFilterContainer, { children: filterOptions.map((letter, index) => (_jsx(AlphabetFilterItem, { active: activeLetter === letter, noDot: filterOptions.length - 1 === index, onClick: () => onFilter(letter), children: letter }, letter))) }));
};
const AlphabetList = ({ data, onSelect, onDelete, onCheckedItemsChange, searchKey, selectedItems = [], filter, onFilteredDataChange, hideCheckbox }) => {
    const [listData, setListData] = useState([]);
    const [activeLetter, setActiveLetter] = useState('');
    const [blinkingLetter, setBlinkingLetter] = useState(null);
    const listRefs = useRef({});
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    const filterOptions = [...alphabet, '#'];
    const groupedData = filterOptions.reduce((acc, letter) => {
        const items = listData?.filter((item) => {
            const itemName = typeof item === 'string' ? item : item.name;
            const firstChar = itemName.charAt(0).toUpperCase();
            return /^[A-Z]$/.test(firstChar) ? firstChar === letter : letter === '#';
        });
        acc[letter] = acc[letter] || [];
        acc[letter] = acc[letter].concat(items);
        return acc;
    }, {});
    const handleFilter = (letter) => {
        setActiveLetter(letter);
        if (listRefs.current[letter]) {
            listRefs.current[letter]?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
            setBlinkingLetter(letter);
            setTimeout(() => {
                setBlinkingLetter(null);
            }, 2000);
        }
    };
    const handleCheckboxChange = (item, isChecked) => {
        const updatedCheckedItems = isChecked
            ? [...selectedItems, item]
            : selectedItems.filter((checkedItem) => checkedItem !== item);
        onCheckedItemsChange?.(updatedCheckedItems);
    };
    const isItemSelected = (item) => selectedItems.includes(typeof item === 'string' ? item : item.id);
    useEffect(() => {
        if (data) {
            let filteredData = data;
            if (filter?.categories?.length > 0) {
                filteredData = filteredData?.filter((item) => item?.categories?.some((category) => filter?.categories?.includes(category)));
            }
            if (filter?.topLocation) {
                filteredData = filteredData?.filter((item) => item?.topLocation === true);
            }
            if (filter?.unTaggedLocation) {
                filteredData = filteredData.filter((item) => !item.nodeId || item.nodeId.trim() === '');
            }
            if (searchKey) {
                filteredData = filteredData?.filter((item) => (typeof item === 'string' ? item : item?.name)?.toLowerCase()?.includes(searchKey?.toLowerCase()));
            }
            setListData(filteredData);
            if (onFilteredDataChange) {
                const currentFilteredIds = listData?.map((item) => item.id);
                const newFilteredIds = filteredData?.map((item) => item.id);
                if (JSON.stringify(currentFilteredIds) !== JSON.stringify(newFilteredIds)) {
                    onFilteredDataChange(filteredData);
                }
            }
        }
    }, [data, filter, searchKey, onFilteredDataChange]);
    return (_jsxs(ListContainer, { children: [_jsx(AlphabetFilter, { onFilter: handleFilter, activeLetter: activeLetter }), _jsx(ListWrapper, { children: filterOptions.map((letter) => (_jsx("div", { ref: (el) => (listRefs.current[letter] = el), children: groupedData[letter]?.length > 0 && (_jsxs(_Fragment, { children: [_jsx(AlphabetHeader, { isBlinking: blinkingLetter === letter, children: letter }), _jsx(List, { dataSource: groupedData[letter], renderItem: (item) => {
                                    const isSelected = isItemSelected(item);
                                    return (_jsx(List.Item, { style: {
                                            cursor: 'pointer',
                                            borderRadius: '5px',
                                            backgroundColor: isSelected ? 'rgba(0,0,0,.08)' : 'transparent',
                                        }, actions: [
                                            !isSelected && onDelete && (_jsx(Icon, { onClick: () => {
                                                    onDelete(typeof item === 'string' ? item : item.id);
                                                }, icon: "trash-01", size: 18 })),
                                        ], children: _jsxs(Flex, { align: "stretch", justify: "start", gap: 10, children: [!hideCheckbox && _jsx(Checkbox, { checked: isSelected, onChange: (e) => handleCheckboxChange(typeof item === 'string' ? item : item.id, e.target.checked) }), _jsx(Flex, { vertical: true, align: "start", justify: "center", style: { flex: 1 }, children: _jsx("h4", { onClick: () => {
                                                            if (onSelect)
                                                                onSelect(typeof item === 'string' ? item : item.id);
                                                        }, children: typeof item === 'string' ? item : item.name }) })] }) }));
                                } })] })) }, letter))) })] }));
};
export default AlphabetList;
